import {useState, useEffect, useCallback, useMemo, useContext} from "react";
import {useSearchCourses, type CourseSearchResultDto} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

interface ILibrarySearch {
	id?: number;
	searchTerm?: string;
	tags?: number[];
	instructorId?: number;
	groupId?: number;
	includeCourses?: boolean;
	includeGroups?: boolean;
	includeEnrolled?: boolean;
	includeUnEnrolled?: boolean;
	includeInProgress?: boolean;
	includeCompleted?: boolean;
}

interface UseCourseLibraryResult {
	coursesData: CourseSearchResultDto[];
	totalCount: number;
	isLoading: boolean;
	isError: boolean;
	searchValue: ILibrarySearch;
	setSearchValue: (value: ILibrarySearch) => void;
	categoryData: { [key: string]: CourseSearchResultDto[] };
	categoryHasMore: { [key: string]: boolean };
	loadMoreDataForCategory: (category: string) => void;
	coursesCategories: string[];
	categoryCounts: Record<string, number>;
}

/**
 * Feature hook for Course Library page
 * 
 * Manages search state, pagination, and category-based data organization
 * Uses the useSearchCourses data layer hook for API calls
 */
export const useCourseLibrary = (): UseCourseLibraryResult => {
	const [searchValue, setSearchValue] = useState<ILibrarySearch>({
		searchTerm: "",
		includeEnrolled: true,
		includeGroups: false,
		includeCourses: true
	});

	const [coursesData, setCoursesData] = useState<CourseSearchResultDto[]>([]);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [categoryPages, setCategoryPages] = useState<{ [key: string]: number }>({});
	const [categoryHasMore, setCategoryHasMore] = useState<{ [key: string]: boolean }>({});
	const [categoryData, setCategoryData] = useState<{ [key: string]: CourseSearchResultDto[] }>({});
	const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

	const coursesCategories = useMemo(() => [
		"Job Training",
		"Personal Development",
		"Professional Development",
		"Safety",
		"Others"
	], []);

	const pageSize = 50;
	const [{locale}] = useContext(LocaleContext);
	const resolvedLocale = resolveLocale(locale);

	// Use the search courses hook from data layer (locale = one course per family in requested language)
	const {data: searchResponse, isLoading, isError} = useSearchCourses({
		search: searchValue.searchTerm || undefined,
		page: currentPage,
		pageSize: pageSize,
		locale: resolvedLocale
	});

	// Reset pagination when search term or locale changes
	useEffect(() => {
		setCurrentPage(1);
		setCategoryPages({});
		setCategoryHasMore({});
		setCoursesData([]);
		setCategoryData({});
	}, [searchValue.searchTerm, resolvedLocale]);

	// Handle course data from the hook
	useEffect(() => {
		if (searchResponse && !isError) {
			const coursesArray = searchResponse.data || [];
			const page = searchResponse.page || 1;

			// For initial search (page 1), set all data
			if (page === 1) {
				setCoursesData(coursesArray);
				setTotalCount(searchResponse.totalCount || 0);
				setCategoryCounts(searchResponse.categoryCounts || {});

				// Initialize category data
				const initialCategoryData: { [key: string]: CourseSearchResultDto[] } = {};
				const initialCategoryHasMore: { [key: string]: boolean } = {};

				coursesCategories.forEach(category => {
					const categoryData = category === "Others"
						? coursesArray.filter((course: CourseSearchResultDto) => {
							// Include courses that don't have contentType or don't belong to defined categories
							const hasNoContentType = !course.contentType || course.contentType === "" || course.contentType === null;
							const belongsToDefinedCategory = ["Job Training", "Personal Development", "Professional Development", "Safety"].includes(course.contentType);
							return hasNoContentType || !belongsToDefinedCategory;
						})
						: coursesArray.filter((course: CourseSearchResultDto) => course.contentType === category);
					initialCategoryData[category] = categoryData;
					initialCategoryHasMore[category] = categoryData.length > 0;
				});

				setCategoryData(initialCategoryData);
				setCategoryHasMore(initialCategoryHasMore);
			} else {
				// Add new data to existing courses data
				setCoursesData(prevData => {
					// Check for duplicates
					const existingIds = new Set(prevData.map(course => course.id));
					const newData = coursesArray.filter(course => !existingIds.has(course.id));
					const updatedData = [...prevData, ...newData];

					if (updatedData.length >= (searchResponse.totalCount || 0) && searchResponse.totalCount > 0) {
						setCategoryHasMore(prev => {
							const updated = {...prev};
							coursesCategories.forEach(cat => {
								updated[cat] = false;
							});
							return updated;
						});
					}

					return updatedData;
				});

				// Update category data
				setCategoryData(prevCategoryData => {
					const updatedCategoryData = {...prevCategoryData};

					coursesCategories.forEach(category => {
						const categoryData = category === "Others"
							? coursesArray.filter((course: CourseSearchResultDto) => {
								const hasNoContentType = !course.contentType || course.contentType === "" || course.contentType === null;
								const belongsToDefinedCategory = ["Job Training", "Personal Development", "Professional Development", "Safety"].includes(course.contentType);
								return hasNoContentType || !belongsToDefinedCategory;
							})
							: coursesArray.filter((course: CourseSearchResultDto) => course.contentType === category);

						if (categoryData.length > 0) {
							const existingCategoryData = updatedCategoryData[category] || [];
							const existingIds = new Set(existingCategoryData.map(course => course.id));
							const newCategoryData = categoryData.filter(course => !existingIds.has(course.id));
							updatedCategoryData[category] = [...existingCategoryData, ...newCategoryData];
						}
					});
					return updatedCategoryData;
				});
			}
		} else if (isError) {
			setCoursesData([]);
			setTotalCount(0);
			setCategoryData({});
			setCategoryHasMore({});
			setCategoryCounts({});
		}
	}, [searchResponse, isError, coursesCategories]);

	const loadMoreDataForCategory = useCallback((category: string) => {
		if (isLoading) return;

		const totalCoursesGlobally = coursesData?.length || 0;
		const totalCountGlobally = totalCount || 0;

		if (totalCoursesGlobally >= totalCountGlobally) {
			setCategoryHasMore(prev => ({...prev, [category]: false}));
			return;
		}

		const currentCategoryPage = categoryPages[category] || 1;
		const nextPage = currentCategoryPage + 1;

		// Update category page for tracking
		setCategoryPages(prev => ({...prev, [category]: nextPage}));
		setCurrentPage(nextPage);
	}, [isLoading, coursesData, totalCount, categoryPages]);

	return {
		coursesData,
		totalCount,
		isLoading,
		isError,
		searchValue,
		setSearchValue,
		categoryData,
		categoryHasMore,
		loadMoreDataForCategory,
		coursesCategories,
		categoryCounts
	};
};

